import { AlertTriangle, Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { isTopMostModal, pushModal, removeModal } from './modalStack';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary';
  loading?: boolean;
  children?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Onayla',
  cancelLabel = 'İptal',
  variant = 'primary',
  loading = false,
  children,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const instanceId = useRef(Symbol('confirm-dialog'));
  const initialFocusDone = useRef(false);
  const previousOpen = useRef(open);

  useEffect(() => {
    if (!open) {
      initialFocusDone.current = false;
      previousOpen.current = false;
      return;
    }

    // Modal ile aynı yığına kaydol: form modalı üzerinde açıldığında
    // ESC yalnız bu diyaloğu kapatsın, alttaki modal açık kalsın.
    const id = instanceId.current;
    pushModal(id);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isTopMostModal(id)) return;

      if (e.key === 'Escape') {
        onCancel();
        return;
      }

      if (e.key !== 'Tab' || !modalRef.current) return;

      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    if (!initialFocusDone.current && !previousOpen.current) {
      modalRef.current?.querySelector<HTMLElement>('button')?.focus();
      initialFocusDone.current = true;
    }
    previousOpen.current = true;

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      removeModal(id);
    };
  }, [open, onCancel]);

  if (!open) return null;

  const confirmBase =
    variant === 'danger'
      ? 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500'
      : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 py-4 sm:items-center"
      role="presentation"
      onClick={onCancel}
    >
      <div
        ref={modalRef}
        data-testid="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby={description ? 'confirm-desc' : undefined}
        className="w-full max-w-none rounded-t-2xl bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-xl sm:max-w-sm sm:rounded-xl sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
              variant === 'danger' ? 'bg-red-100' : 'bg-blue-100'
            }`}
          >
            <AlertTriangle
              className={`h-5 w-5 ${variant === 'danger' ? 'text-red-600' : 'text-blue-600'}`}
            />
          </div>
          <div>
            <h3 id="confirm-title" className="text-lg font-semibold text-slate-900">
              {title}
            </h3>
            {description && (
              <p id="confirm-desc" className="mt-1 text-sm text-slate-500">
                {description}
              </p>
            )}
            {children}
          </div>
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            onClick={onCancel}
            disabled={loading}
            data-testid="cancel-button"
            className="min-h-[44px] rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            data-testid="confirm-button"
            className={`flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 ${confirmBase}`}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
